import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Button } from 'components/ui/Button/Button'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { OtherText, TitleText } from 'styling/Typography'
import { IOpsTodayHandledEntry, IOpsTodayInboxAction, IOpsTodayInboxItem } from 'types/opsToday.interface'
import {
  EmptyInboxTitle,
  EmptyInboxWrap,
  HandledGlyph,
  HandledList,
  HandledMark,
  HandledRow,
  HandledSection,
  HandledText,
  HandledToggle,
  InboxActions,
  InboxChevron,
  InboxDetailActions,
  InboxDetailBlock,
  InboxDetailLabel,
  InboxDetailLine,
  InboxDetailValue,
  InboxDot,
  InboxList,
  InboxRow,
  InboxSentence,
  InboxSource,
  InboxTextWrap,
} from './OpsToday.styled'

const HANDLED_TIMES: { [id: number]: string } = { 1: '07:55', 2: '07:48', 3: '07:52', 4: '07:58' }

const OUTCOME_META: { [k in IOpsTodayHandledEntry['outcome']]: { mark: string; label: string } } = {
  approved: { mark: '✓', label: 'Approved' },
  dismissed: { mark: '✕', label: 'Dismissed' },
  acknowledged: { mark: '✓', label: 'Acknowledged' },
}

const isConfirmFlow = (item: IOpsTodayInboxItem) =>
  Boolean(item.detail) && item.actions.some((a) => a.variant === 'success' && !a.navigateTo)

const outcomeFor = (action: IOpsTodayInboxAction): IOpsTodayHandledEntry['outcome'] => {
  if (action.variant === 'black') return 'dismissed'
  if (action.label === 'OK') return 'acknowledged'
  return 'approved'
}

export const TodayInbox = ({ initialItems }: { initialItems: IOpsTodayInboxItem[] }) => {
  const [items, setItems] = useState(initialItems)
  const [handled, setHandled] = useState<IOpsTodayHandledEntry[]>([])
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [handledOpen, setHandledOpen] = useState(false)
  const navigate = useNavigate()

  const toggleExpand = (id: number) => setExpandedId((prev) => (prev === id ? null : id))

  const handleAction = (item: IOpsTodayInboxItem, action: IOpsTodayInboxAction) => {
    if (action.toastMessage) toast.success(action.toastMessage)
    if (!action.keepsItem) {
      setItems((prev) => prev.filter((i) => i.id !== item.id))
      setHandled((prev) => [{ item, outcome: outcomeFor(action), at: HANDLED_TIMES[item.id] || '07:55' }, ...prev])
      setExpandedId((prev) => (prev === item.id ? null : prev))
    }
    if (action.navigateTo) navigate(action.navigateTo)
  }

  const handleUndo = (entry: IOpsTodayHandledEntry) => {
    setHandled((prev) => prev.filter((h) => h.item.id !== entry.item.id))
    setItems((prev) =>
      [...prev, entry.item].sort(
        (a, b) => initialItems.findIndex((i) => i.id === a.id) - initialItems.findIndex((i) => i.id === b.id),
      ),
    )
  }

  return (
    <Container>
      <ContainerHeader>
        <TitleText>Needs you</TitleText>
      </ContainerHeader>
      <ContainerContent>
        {items.length === 0 ? (
          <EmptyInboxWrap>
            <EmptyInboxTitle>✓ Nothing needs you right now.</EmptyInboxTitle>
            <OtherText grey>We will add anything that needs a decision here.</OtherText>
          </EmptyInboxWrap>
        ) : (
          <InboxList>
            {items.map((item) => {
              const expanded = expandedId === item.id
              const confirmFlow = isConfirmFlow(item)
              return (
                <InboxRow key={item.id}>
                  <InboxDot $tone={item.tone} />
                  <InboxTextWrap>
                    <InboxSentence>{item.sentence}</InboxSentence>
                    <InboxSource>{item.source}</InboxSource>
                  </InboxTextWrap>
                  <InboxActions>
                    {confirmFlow
                      ? !expanded && (
                        <Button size='sm' variant='secondary' onClick={() => toggleExpand(item.id)}>
                          Review
                        </Button>
                      )
                      : item.actions.map((action) => (
                        <Button
                          key={action.label}
                          size='sm'
                          variant={action.variant}
                          onClick={() => handleAction(item, action)}
                        >
                          {action.label}
                        </Button>
                      ))}
                    {item.detail && (
                      <InboxChevron $open={expanded} onClick={() => toggleExpand(item.id)}>
                        ▾
                      </InboxChevron>
                    )}
                  </InboxActions>
                  {item.detail && expanded && (
                    <InboxDetailBlock>
                      <InboxDetailLine>
                        <InboxDetailLabel>Why</InboxDetailLabel>
                        <InboxDetailValue>{item.detail.why}</InboxDetailValue>
                      </InboxDetailLine>
                      <InboxDetailLine>
                        <InboxDetailLabel>Impact</InboxDetailLabel>
                        <InboxDetailValue>{item.detail.impact}</InboxDetailValue>
                      </InboxDetailLine>
                      <InboxDetailLine>
                        <InboxDetailLabel>What happens</InboxDetailLabel>
                        <InboxDetailValue>{item.detail.whatHappens}</InboxDetailValue>
                      </InboxDetailLine>
                      {confirmFlow && (
                        <InboxDetailActions>
                          {item.actions.map((action) => (
                            <Button
                              key={action.label}
                              size='sm'
                              variant={action.variant}
                              onClick={() => handleAction(item, action)}
                            >
                              {action.label}
                            </Button>
                          ))}
                        </InboxDetailActions>
                      )}
                    </InboxDetailBlock>
                  )}
                </InboxRow>
              )
            })}
          </InboxList>
        )}
        {handled.length > 0 && (
          <HandledSection>
            <HandledToggle onClick={() => setHandledOpen((prev) => !prev)}>
              Handled this shift ({handled.length})
              <HandledGlyph $open={handledOpen}>▾</HandledGlyph>
            </HandledToggle>
            {handledOpen && (
              <HandledList>
                {handled.map((entry) => (
                  <HandledRow key={entry.item.id}>
                    <HandledMark $outcome={entry.outcome}>{OUTCOME_META[entry.outcome].mark}</HandledMark>
                    <HandledText>
                      {OUTCOME_META[entry.outcome].label} — {entry.item.sentence} · by you at {entry.at}
                    </HandledText>
                    <Button size='sm' variant='black' onClick={() => handleUndo(entry)}>
                      Undo
                    </Button>
                  </HandledRow>
                ))}
              </HandledList>
            )}
          </HandledSection>
        )}
      </ContainerContent>
    </Container>
  )
}
